import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher, useSearchParams } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import {
	RESET_PASSWORD_FORM,
	RESET_PASSWORD_INPUTS,
	resetPasswordSchema,
} from '#app/components/forms/reset-password-form/reset-password-form.tsx'
import { RESET_PASSWORD_I18NEXT } from '#app/routes/($lang)._auth.reset-password/route-config'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'

type UseResetPasswordProps = {
	app: App
}

function useResetPasswordForm({ app }: UseResetPasswordProps) {
	const [searchParams] = useSearchParams()
	const { t } = useTranslation(RESET_PASSWORD_I18NEXT.routeAuthResetPassword)

	const fetcherKey = RESET_PASSWORD_FORM.fetcherKey
	const fetcher = useFetcher({ key: fetcherKey })

	const formValues = {
		[ROOT_INPUTS.subaction]: RESET_PASSWORD_FORM.subaction,
		[ROOT_INPUTS.noJs]: String(app.options.noJs || true),
		...(app.options.redirect && {
			[ROOT_INPUTS.redirect]: app.options.redirect,
		}),
	}
	const [form, fields] = useForm({
		lastResult: fetcher.data || undefined,
		defaultValue: {
			[RESET_PASSWORD_INPUTS.email]:
				searchParams.get(RESET_PASSWORD_INPUTS.email) ||
				app.options.data?.[RESET_PASSWORD_INPUTS.email] ||
				undefined,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: resetPasswordSchema })
		},
		shouldValidate: 'onInput',
	})

	return {
		resetPasswordFetcherKey: fetcherKey,
		resetPasswordFetcher: fetcher,
		resetPasswordFormValues: formValues,
		resetPasswordForm: form,
		resetPasswordFields: fields,
		t,
		searchParams,
	}
}

export { type UseResetPasswordProps, useResetPasswordForm }
